import { useLocation } from "react-router-dom";
import { Reveal } from "../components/common";
import { LEGAL } from "../data/legalContent";
import { Seo, breadcrumbs } from "../components/Seo";

function renderInline(text, keyPrefix) {
  // split on **bold** and [text](url)
  const tokens = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return tokens.map((tok, i) => {
    const key = `${keyPrefix}-${i}`;
    if (tok.startsWith("**") && tok.endsWith("**")) {
      return <strong key={key} className="text-white font-semibold">{tok.slice(2, -2)}</strong>;
    }
    const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return <a key={key} href={link[2]} target="_blank" rel="noreferrer" className="text-lime hover:underline">{link[1]}</a>;
    }
    return <span key={key}>{tok}</span>;
  });
}

function Markdown({ body }) {
  const lines = body.split("\n");
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 space-y-2 text-white/80 my-4">
          {list.map((li, i) => <li key={i}>{renderInline(li, `li-${blocks.length}-${i}`)}</li>)}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) { list.push(line.slice(2)); return; }
    flush();
    if (!line.trim()) return;
    if (line.startsWith("## ")) {
      blocks.push(<h2 key={idx} className="text-2xl font-bold text-white mt-10 mb-4">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      blocks.push(<h1 key={idx} className="text-3xl font-black mt-6 mb-4">{line.slice(2)}</h1>);
    } else if (line.startsWith("---")) {
      blocks.push(<hr key={idx} className="border-[#808080]/20 my-8" />);
    } else if (line.startsWith("*") && line.endsWith("*") && !line.startsWith("**")) {
      blocks.push(<p key={idx} className="text-grey text-sm italic">{line.replace(/\*/g, "")}</p>);
    } else {
      blocks.push(<p key={idx} className="text-white/80 leading-relaxed my-3">{renderInline(line, `p-${idx}`)}</p>);
    }
  });
  flush();
  return <div>{blocks}</div>;
}

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/\//g, "");
  const doc = LEGAL[slug];
  if (!doc) {
    return <div className="pt-40 pb-32 container-x mx-auto max-w-[1400px] text-center"><h1 className="text-4xl font-black">Page not found</h1></div>;
  }
  return (
    <div data-testid={`legal-${slug}-page`} className="pt-32">
      <Seo title={doc.title} path={pathname} description={`${doc.title} for DNA Finance Ltd, UK commercial finance broker. Company number 16322517, ICO registration ZC234972.`} jsonLd={breadcrumbs([{ name: "Home", path: "/" }, { name: doc.title, path: pathname }])} />
      <section className="container-x mx-auto max-w-[820px] py-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-10">{doc.title}</h1>
          <Markdown body={doc.body} />
        </Reveal>
      </section>
    </div>
  );
}
